import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Package, DollarSign, CheckCircle2, Circle, Truck, Mail, Star, X, Check } from "lucide-react";
import AdminCatalog from "./AdminCatalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, Icon }) => (
  <div className="border border-off-white/10 p-5 bg-off-white/[0.02]">
    <div className="flex items-center justify-between mb-3">
      <span className="font-mono text-[10px] tracking-[0.3em] text-rose">// {label}</span>
      <Icon size={16} className="text-off-white/50" strokeWidth={1.5} />
    </div>
    <p className="font-display text-3xl md:text-4xl text-off-white">{value}</p>
  </div>
);

const StarRow = ({ value }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={12} className={i < value ? "text-rose" : "text-off-white/20"} fill={i < value ? "currentColor" : "none"} />
    ))}
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("whipitup_admin_token") : null;
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | paid | unfulfilled
  const [view, setView] = useState("orders");  // orders | reviews

  const authHeader = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s, r] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: authHeader }),
        axios.get(`${API}/admin/stats`, { headers: authHeader }),
        axios.get(`${API}/admin/reviews`, { headers: authHeader }),
      ]);
      setOrders(o.data); setStats(s.data); setReviews(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("whipitup_admin_token");
        navigate("/admin/login");
      }
    } finally { setLoading(false); }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    load();
  }, [token, navigate, load]);

  const logout = () => {
    localStorage.removeItem("whipitup_admin_token");
    navigate("/admin/login");
  };

  const toggleFulfilled = async (session_id, next) => {
    try {
      await axios.patch(`${API}/admin/orders/${session_id}`, { fulfilled: next }, { headers: authHeader });
      setOrders((prev) => prev.map((o) => (o.session_id === session_id ? { ...o, fulfilled: next } : o)));
      const s = await axios.get(`${API}/admin/stats`, { headers: authHeader });
      setStats(s.data);
    } catch (_e) { /* noop */ }
  };

  const moderateReview = async (review_id, status) => {
    try {
      await axios.patch(`${API}/admin/reviews/${review_id}`, { status }, { headers: authHeader });
      setReviews((prev) => prev.map((r) => (r.review_id === review_id ? { ...r, status } : r)));
      const s = await axios.get(`${API}/admin/stats`, { headers: authHeader });
      setStats(s.data);
    } catch (_e) { /* noop */ }
  };

  const filtered = orders.filter((o) => {
    if (filter === "paid") return o.payment_status === "paid";
    if (filter === "unfulfilled") return o.payment_status === "paid" && !o.fulfilled;
    return true;
  });

  return (
    <main className="min-h-screen bg-charcoal pt-8" data-testid="admin-dashboard">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-4 py-8 border-b border-off-white/10">
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-rose mb-2">// ADMIN · BACKOFFICE</p>
            <h1 className="font-serif-display italic text-4xl md:text-6xl text-off-white leading-[0.95]">
              {view === "orders" ? "Orders & fulfillment." : view === "reviews" ? "Review moderation." : "Catalog management."}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-[11px] tracking-[0.25em] text-off-white/70 hover:text-rose transition-colors-only">STORE →</Link>
            <button onClick={logout} className="h-11 px-4 border border-off-white/20 hover:border-rose hover:text-rose flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] transition-colors-only" data-testid="admin-logout-btn">
              <LogOut size={14} /> LOGOUT
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 py-8">
          <StatCard label="REVENUE" value={stats ? `$${stats.revenue_total.toFixed(2)}` : "—"} Icon={DollarSign} />
          <StatCard label="PAID ORDERS" value={stats ? stats.orders_paid : "—"} Icon={Package} />
          <StatCard label="UNFULFILLED" value={stats ? stats.orders_unfulfilled : "—"} Icon={Truck} />
          <StatCard label="UNITS SOLD" value={stats ? stats.units_sold : "—"} Icon={CheckCircle2} />
          <StatCard label="PENDING REVIEWS" value={stats ? stats.reviews_pending : "—"} Icon={Star} />
        </div>

        {/* Section switcher */}
        <div className="flex items-center gap-2 border-b border-off-white/10 pb-2 mb-4">
          {[
            { key: "orders", label: "ORDERS" },
            { key: "reviews", label: "REVIEWS" },
            { key: "catalog", label: "CATALOG" },
          ].map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`h-10 px-4 font-mono text-[10px] tracking-[0.25em] transition-colors-only ${
                view === v.key ? "text-rose border-b-2 border-rose" : "text-off-white/50 hover:text-off-white"
              }`}
              data-testid={`view-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {view === "orders" && (
          <>
            <div className="flex flex-wrap items-center gap-2 pb-4 border-b border-off-white/10">
              {[
                { key: "all", label: "ALL" },
                { key: "paid", label: "PAID" },
                { key: "unfulfilled", label: "TO SHIP" }
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`h-9 px-4 border font-mono text-[10px] tracking-[0.25em] transition-colors-only ${
                    filter === f.key ? "bg-rose text-charcoal border-rose" : "border-off-white/20 text-off-white/70 hover:border-off-white"
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label}
                </button>
              ))}
              <button onClick={load} className="h-9 px-3 ml-auto font-mono text-[10px] tracking-[0.25em] text-off-white/60 hover:text-rose transition-colors-only">
                {loading ? "REFRESHING…" : "REFRESH"}
              </button>
            </div>

            <div className="py-8">
              {loading && orders.length === 0 && (
                <div className="flex items-center justify-center gap-3 py-16 text-off-white/60">
                  <Loader2 size={16} className="animate-spin text-rose" />
                  <span className="font-mono text-[11px] tracking-[0.25em]">LOADING ORDERS…</span>
                </div>
              )}

              {!loading && filtered.length === 0 && (
                <p className="py-16 text-center font-mono text-[11px] tracking-[0.25em] text-off-white/40">// NO ORDERS IN THIS VIEW</p>
              )}

              <ul className="space-y-3" data-testid="admin-orders-list">
                {filtered.map((o) => (
                  <li key={o.session_id} className="border border-off-white/10 bg-off-white/[0.02] p-5">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                      <div className="md:col-span-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${o.payment_status === "paid" ? "bg-rose" : "bg-off-white/30"}`} />
                          <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/60">
                            {o.payment_status === "paid" ? "PAID" : (o.payment_status || "PENDING").toUpperCase()}
                          </span>
                          {o.fulfilled && (
                            <span className="font-mono text-[9px] tracking-[0.25em] px-2 py-0.5 bg-rose/20 text-rose border border-rose/40">SHIPPED</span>
                          )}
                        </div>
                        <Link to={`/admin/order/${o.session_id}`} className="block font-mono text-[10px] tracking-[0.2em] text-off-white/40 hover:text-rose transition-colors-only mt-2 break-all" data-testid={`order-detail-link-${o.session_id.slice(-8)}`}>
                          {o.session_id}
                        </Link>
                        <p className="text-off-white/70 text-sm mt-1 flex items-center gap-2">
                          <Mail size={12} className="text-off-white/40" />
                          {o.customer_name ? `${o.customer_name} · ` : ""}{o.customer_email || "— no email —"}
                        </p>
                        <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/50 mt-1">
                          {o.created_at ? new Date(o.created_at).toLocaleString() : ""}
                        </p>
                        {o.tracking_number && (
                          <p className="font-mono text-[10px] tracking-[0.25em] text-rose mt-1">
                            {o.tracking_carrier || "TRK"} · {o.tracking_number}
                          </p>
                        )}
                      </div>

                      <div className="md:col-span-5">
                        <ul className="space-y-1">
                          {o.items.map((it, i) => (
                            <li key={i} className="flex justify-between text-sm text-off-white/85">
                              <span>{it.qty} × {it.name}</span>
                              <span className="font-mono text-off-white/60">${it.line_total?.toFixed(2)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="md:col-span-3 flex flex-col md:items-end gap-3">
                        <div className="text-right">
                          <p className="font-mono text-[10px] tracking-[0.25em] text-off-white/50">TOTAL</p>
                          <p className="font-display text-2xl text-off-white">${o.amount?.toFixed(2)}</p>
                        </div>
                        <div className="flex gap-2">
                          <Link
                            to={`/admin/order/${o.session_id}`}
                            className="h-10 px-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border border-off-white/20 hover:border-rose hover:text-rose text-off-white/80 transition-colors-only"
                            data-testid={`open-order-${o.session_id.slice(-8)}`}
                          >
                            OPEN
                          </Link>
                          {o.payment_status === "paid" && (
                            <button
                              onClick={() => toggleFulfilled(o.session_id, !o.fulfilled)}
                              className={`h-10 px-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border transition-colors-only ${
                                o.fulfilled
                                  ? "border-rose/40 text-rose hover:bg-rose/10"
                                  : "border-off-white/20 text-off-white hover:border-rose hover:text-rose"
                              }`}
                              data-testid={`toggle-ship-${o.session_id.slice(-8)}`}
                            >
                              {o.fulfilled ? <><CheckCircle2 size={14} /> SHIPPED</> : <><Circle size={14} /> SHIP</>}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {view === "reviews" && (
          <div className="py-8">
            {loading && reviews.length === 0 && (
              <div className="flex items-center justify-center gap-3 py-16 text-off-white/60">
                <Loader2 size={16} className="animate-spin text-rose" />
                <span className="font-mono text-[11px] tracking-[0.25em]">LOADING REVIEWS…</span>
              </div>
            )}
            {!loading && reviews.length === 0 && (
              <p className="py-16 text-center font-mono text-[11px] tracking-[0.25em] text-off-white/40">// NO REVIEWS SUBMITTED YET</p>
            )}
            <ul className="space-y-3" data-testid="admin-reviews-list">
              {reviews.map((r) => (
                <li key={r.review_id} className="border border-off-white/10 bg-off-white/[0.02] p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <StarRow value={r.rating} />
                        <span className={`font-mono text-[10px] tracking-[0.25em] px-2 py-0.5 border ${
                          r.status === "approved" ? "border-rose/40 text-rose"
                          : r.status === "rejected" ? "border-off-white/20 text-off-white/40"
                          : "border-off-white/40 text-off-white/80"
                        }`}>{r.status.toUpperCase()}</span>
                        <span className="font-mono text-[10px] tracking-[0.25em] text-off-white/50">
                          {r.productId.toUpperCase()} · {r.user_name}
                        </span>
                      </div>
                      {r.title && <p className="mt-2 font-serif-display italic text-lg text-off-white">{r.title}</p>}
                      <p className="mt-1 text-off-white/80 text-sm">{r.body}</p>
                      <p className="mt-2 font-mono text-[10px] tracking-[0.25em] text-off-white/40">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {r.status !== "approved" && (
                        <button
                          onClick={() => moderateReview(r.review_id, "approved")}
                          className="h-10 px-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border border-rose/40 text-rose hover:bg-rose hover:text-charcoal transition-colors-only"
                          data-testid={`approve-review-${r.review_id}`}
                        >
                          <Check size={14} /> APPROVE
                        </button>
                      )}
                      {r.status !== "rejected" && (
                        <button
                          onClick={() => moderateReview(r.review_id, "rejected")}
                          className="h-10 px-3 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] border border-off-white/20 text-off-white/70 hover:border-off-white hover:text-off-white transition-colors-only"
                          data-testid={`reject-review-${r.review_id}`}
                        >
                          <X size={14} /> REJECT
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {view === "catalog" && <AdminCatalog token={token} />}
      </div>
    </main>
  );
};

export default AdminDashboard;
